/**
 * Issuing SPV Bank Details Component
 * Handles bank account information collection for supplier/distributor onboarding
 */

import {
  Button,
  color,
  InputGroup,
  InputSelect,
} from "@funded-here-interface/common";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useStyle } from "../../styles/BankDetailsCss";
import { RootState } from "../../store";
import { bankAccountTypes } from "../../constants/bank";
import BankAccNameDisclaimer from "../../components/BankAccNameDisclaimer/BankAccNameDisclaimer";
import useGetOrgDraft from "../../hooks/useGetOrgDraft";
import useSaveOrgDraft from "../../hooks/useSaveOrgDraft";

import { hydrateDisBizState } from "../../features/distributor/biz";
import {
  countries,
  AccountCurrencyTypeEnum,
} from "@funded-here-interface/common/src/constant/country";
import { useBankFormState } from "./hooks/useBankFormState";
import { useBankReduxSync } from "./hooks/useBankReduxSync";
import {
  validateBankForm,
  validateAccountHolderNameMatchesCompany,
} from "./utils/validation";

const currencyOptions = Object.values(AccountCurrencyTypeEnum);

const DistributorBank: FC = () => {
  const { classes } = useStyle();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux selectors
  const userId = useSelector((state: RootState) => state.auth.userId);
  const token = useSelector((state: RootState) => state.auth.token);
  const { company } = useSelector((state: RootState) => state.disBiz);
  const {
    bankName: bankNameRedux,
    accountNumber: accountNoRedux,
    accountType: accountTypeRedux,
    accountHolderName: accountHolderNameRedux,
    swiftCode: swiftCodeRedux,
  } = useSelector((state: RootState) => state.disBank);

  // API hooks
  const getOrgDraftMutation = useGetOrgDraft();
  const saveOrgDraftMutation = useSaveOrgDraft();

  // Custom hooks
  const {
    formState,
    errors,
    setAllErrors,
    setFormData,
    createChangeHandler,
    createNumericChangeHandler,
    createSwiftCodeChangeHandler,
  } = useBankFormState({
    bankName: bankNameRedux || "",
    accountNumber: accountNoRedux || "",
    accountType: accountTypeRedux || "",
    swiftCode: swiftCodeRedux || "",
    accountHolderName: accountHolderNameRedux || "",
    bankAddress: "",
    bankCountry: "",
    currency: "",
  });

  const { syncBankDetails } = useBankReduxSync();

  // Local state for account holder name validation error
  const [accountHolderNameError, setAccountHolderNameError] = useState("");

  /**
   * Handle back navigation - sync form state to Redux
   */
  const handleBack = () => {
    syncBankDetails(formState);
    navigate("/supplier-biz-profile");
  };

  /**
   * Handle next navigation - validate and sync form state
   */
  const handleNext = async () => {
    saveDraft(false);

    // Validate form fields
    const validation = validateBankForm(formState);

    // Additional validation: Check if account holder name matches company name
    if (validation.isValid) {
      const nameMatches = validateAccountHolderNameMatchesCompany(
        formState.accountHolderName,
        company || undefined
      );

      if (!nameMatches) {
        toast.warn("Account Holder Name should be the same as company name");
        return;
      }
    }

    if (validation.isValid) {
      syncBankDetails(formState);
      navigate("/supplier-doc-upload");
    } else {
      setAllErrors(validation.errors);
    }
  };

  /**
   * Fetch and initialize draft data on component mount
   */
  useEffect(() => {
    const initializeDraft = async () => {
      if (!userId) return;

      try {
        const data = await getOrgDraftMutation.mutateAsync({
          userId: parseInt(userId),
          token,
        });

        if (data) {
          // Update form state
          setFormData({
            bankName: data.bankName,
            accountType: data.accountType,
            accountNumber: data.accountNumber,
            accountHolderName: data.accountHolderName,
            swiftCode: data.swiftCode,
            bankAddress: data.bankAddress,
            bankCountry: data.bankCountry,
            currency: data.currency,
          });

          // Sync bank details to Redux
          syncBankDetails({
            bankName: data.bankName,
            accountType: data.accountType,
            accountNumber: data.accountNumber,
            accountHolderName: data.accountHolderName,
            swiftCode: data.swiftCode,
            bankAddress: data.bankAddress,
            bankCountry: data.bankCountry,
            currency: data.currency,
          });

          // Update business info in Redux using hydrate pattern
          dispatch(
            hydrateDisBizState({
              company: data.companyName ?? null,
              regNumber: data.companyRegistrationNo ?? null,
              legalStructure: data.legalStructure ?? null,
              address: data.address ?? null,
              postal: data.postalCode?.toString() ?? null,
              industryGroup: data.industryGroup ?? null,
              typeOfInstitute: data.industry ?? null,
              userFirstName: data.userFirstName ?? null,
              userLastName: data.userLastName ?? null,
            })
          );
        }
      } catch (error) {
        toast.error(`Error fetching draft: ${error}`);
      }
    };

    initializeDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Validate account holder name matches company name
   * @returns true if valid or empty, false if invalid
   */
  const validateAndShowAccountHolderError = (): boolean => {
    const isValid = validateAccountHolderNameMatchesCompany(
      formState.accountHolderName,
      company || undefined
    );

    if (formState.accountHolderName && !isValid) {
      setAccountHolderNameError(
        "Account holder name must match the company name"
      );
      toast.error("Account holder name must match the company name", {
        autoClose: 2000,
      });
      return false;
    }

    setAccountHolderNameError("");
    return true;
  };

  /**
   * Check if any bank fields (excluding accountHolderName) have values
   */
  const hasOtherBankFields = (): boolean => {
    return !!(
      formState.bankName ||
      formState.accountNumber ||
      formState.accountType ||
      formState.swiftCode ||
      formState.bankAddress ||
      formState.bankCountry ||
      formState.currency
    );
  };

  /**
   * Build draft payload, conditionally including accountHolderName
   */
  const buildDraftPayload = (includeAccountHolder: boolean) => ({
    userId: parseInt(userId),
    token,
    bankName: formState.bankName,
    accountNumber: formState.accountNumber,
    accountType: formState.accountType,
    swiftCode: formState.swiftCode,
    bankAddress: formState.bankAddress,
    bankCountry: formState.bankCountry,
    currency: formState.currency,
    ...(includeAccountHolder && {
      accountHolderName: formState.accountHolderName,
    }),
  });

  /**
   * Save draft to backend
   */
  const saveDraft = (showToast: boolean = true) => {
    const isAccountHolderValid = validateAndShowAccountHolderError();
    const hasOtherFields = hasOtherBankFields();

    // Skip save if only invalid account holder name is filled
    if (
      formState.accountHolderName &&
      !isAccountHolderValid &&
      !hasOtherFields
    ) {
      return;
    }

    const payload = buildDraftPayload(isAccountHolderValid);

    saveOrgDraftMutation.mutateAsync(payload, {
      onSuccess: () => {
        if (showToast && (hasOtherFields || isAccountHolderValid)) {
          toast.success("Draft Saved", { autoClose: 2000 });
        }
      },
      onError: (e) => {
        toast.error((e as Error).message);
      },
    });
  };

  return (
    <div style={{ marginTop: "70px" }}>
      <BankAccNameDisclaimer />
      <InputGroup
        isErrored={errors.accountHolderName || accountHolderNameError}
        errorMessage={!!(errors.accountHolderName || accountHolderNameError)}
        onChange={(value: string) => {
          createChangeHandler("accountHolderName")(value);
          if (accountHolderNameError) {
            setAccountHolderNameError("");
          }
        }}
        labelText="Account Holder Name"
        value={formState.accountHolderName}
        id="account_holder_name"
      />
      <InputGroup
        isErrored={errors.bankName}
        errorMessage
        onChange={createChangeHandler("bankName")}
        labelText="Bank Name"
        id="bank_name"
        value={formState.bankName}
      />
      <InputGroup
        isErrored={errors.accountNumber}
        errorMessage
        onChange={createNumericChangeHandler("accountNumber")}
        labelText="Bank Account Number"
        id="account_number"
        value={formState.accountNumber}
      />
      <InputSelect
        isErrored={errors.accountType}
        errorMessage
        onChange={createChangeHandler("accountType")}
        labelText="Bank Account Type"
        id="accountType"
        value={formState.accountType}
        data={bankAccountTypes}
      />
      <InputGroup
        isErrored={errors.swiftCode}
        errorMessage
        onChange={createSwiftCodeChangeHandler("swiftCode")}
        labelText="Bank SWIFT Code"
        value={formState.swiftCode}
        id="swift_code"
      />
      <InputGroup
        isErrored={errors.bankAddress}
        errorMessage
        onChange={createChangeHandler("bankAddress")}
        labelText="Bank Address"
        value={formState.bankAddress}
        id="bank_address"
      />
      <InputSelect
        isErrored={errors.bankCountry}
        errorMessage
        onChange={createChangeHandler("bankCountry")}
        labelText="Bank Country"
        id="bank_country"
        value={formState.bankCountry}
        data={countries}
      />
      <InputSelect
        isErrored={errors.currency}
        errorMessage
        onChange={createChangeHandler("currency")}
        labelText="Currency"
        id="currency"
        value={formState.currency}
        data={currencyOptions}
      />
      <div className={classes.buttonDiv}>
        <div className={classes.buttonWrapper}>
          <Button
            onClick={handleBack}
            backgroundColor="#ffff"
            textColor="#338F86"
            children="Back"
            border="1px solid #338F86"
            width="100%"
          />
        </div>

        <ToastContainer />
        <div className={classes.buttonWrapper}>
          <Button
            onClick={handleNext}
            textColor="#ffff"
            backgroundColor="#338F86"
            children="Next"
            width="100%"
          />
        </div>
      </div>
      <div className={classes.buttonWrapper}>
        <Button
          onClick={() => saveDraft()}
          textColor={color.WHITE}
          backgroundColor={color.LOANTEXT}
          children="Save Draft"
          width="118%"
        />
      </div>
    </div>
  );
};

export default DistributorBank;
